<!--
  @component LiquidationToleranceConfig
  @description Configuración de tolerancia de redondeo para liquidaciones.
               Permite definir una tolerancia absoluta (en pesos) o porcentual
               para la comparación entre importe esperado e importe facturado.
-->
<template>
  <div class="ltc">
    <div class="ltc__header">
      <h2 class="ltc__title">Tolerancia de liquidaciones</h2>
      <p class="ltc__desc">
        Define el margen de diferencia aceptable entre el importe esperado según
        contrato y el importe facturado por el prestador.
      </p>
    </div>

    <div class="ltc__card" v-loading="isLoading">
      <div class="ltc__field-group">
        <div class="ltc__field-label">Tipo de tolerancia</div>
        <el-radio-group
          v-model="form.toleranceType"
          size="small"
          @change="onTypeChange"
        >
          <el-radio-button label="absolute">Valor absoluto ($)</el-radio-button>
          <el-radio-button label="percentage">Porcentaje (%)</el-radio-button>
        </el-radio-group>
      </div>

      <div class="ltc__field-group" style="margin-top: 20px">
        <div class="ltc__field-label">
          {{
            form.toleranceType === "absolute"
              ? "Tolerancia en pesos"
              : "Tolerancia en porcentaje"
          }}
        </div>
        <div class="ltc__input-row">
          <el-input-number
            v-model="form.toleranceValue"
            :min="0"
            :precision="form.toleranceType === 'absolute' ? 2 : 1"
            :step="form.toleranceType === 'absolute' ? 1 : 0.5"
            size="small"
            style="width: 180px"
          />
          <span class="ltc__unit">{{
            form.toleranceType === "absolute" ? "ARS" : "%"
          }}</span>
        </div>
        <div class="ltc__hint">
          <span v-if="form.toleranceType === 'absolute'">
            Una diferencia de hasta
            <strong>$ {{ form.toleranceValue || 0 }}</strong> entre lo esperado
            y lo facturado se considerará válida.
          </span>
          <span v-else>
            Una diferencia de hasta
            <strong>{{ form.toleranceValue || 0 }}%</strong> sobre el importe
            esperado se considerará válida.
          </span>
        </div>
      </div>

      <div class="ltc__actions">
        <el-button type="primary" size="small" :loading="saving" @click="save">
          Guardar configuración
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import liquidationService from "@/services/liquidation";

/** @component LiquidationToleranceConfig */
export default {
  name: "LiquidationToleranceConfig",
  data() {
    return {
      isLoading: false,
      saving: false,
      form: {
        toleranceType: "absolute",
        toleranceValue: 0,
      },
    };
  },
  created() {
    this.loadConfig();
  },
  methods: {
    /** @description Carga la configuración de tolerancia actual */
    async loadConfig() {
      this.isLoading = true;
      try {
        const config = await liquidationService.getToleranceConfig();
        this.form.toleranceType = config.toleranceType || "absolute";
        this.form.toleranceValue = config.toleranceValue || 0;
      } catch (e) {
        this.$message.error("Error al cargar configuración");
      } finally {
        this.isLoading = false;
      }
    },
    /** @description Resetea el valor al cambiar el tipo de tolerancia */
    onTypeChange() {
      this.form.toleranceValue = 0;
    },
    /** @description Guarda la configuración de tolerancia */
    async save() {
      this.saving = true;
      try {
        await liquidationService.saveToleranceConfig({
          toleranceType: this.form.toleranceType,
          toleranceValue: this.form.toleranceValue,
        });
        this.$message.success("Configuración guardada");
      } catch (e) {
        this.$message.error("Error al guardar configuración");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ltc {
  font-family: "Plus Jakarta Sans", sans-serif;
}
.ltc__header {
  margin-bottom: 24px;
}
.ltc__title {
  font-size: 18px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 6px;
}
.ltc__desc {
  font-size: 13px;
  color: #6b7294;
  margin: 0;
}
.ltc__card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 28px;
  max-width: 480px;
}
.ltc__field-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  margin-bottom: 8px;
}
.ltc__input-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ltc__unit {
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;
}
.ltc__hint {
  margin-top: 10px;
  font-size: 12px;
  color: #94a3b8;
}
.ltc__hint strong {
  color: #002d5e;
}
.ltc__actions {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #f0f2f7;
}
</style>
